import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { dashboardAPI } from '../../services';
import './styles/dashboard.css';

import TotalSalesImg from "../../assets/total-sales-img.svg";
import NetProfitImg from "../../assets/net-profit-img.svg";
import CreditCustomersImg from "../../assets/credit-customers-img.svg";
import TotalOrdersImg from "../../assets/total-orders-img.svg";

const formatChange = (val) => {
  if (val === null || val === undefined || val === '') return '--';
  const num = Number(val);
  if (Number.isNaN(num)) return val;
  const signed = num > 0 ? `+${num}` : `${num}`;
  return `${signed}%`;
};

const formatValue = (val, prefix = '') => {
  if (val === null || val === undefined) return '--';
  if (typeof val === 'object') return '--';
  const num = Number(val);
  if (Number.isNaN(num)) return `${prefix}${val}`;
  return `${prefix}${num.toLocaleString()}`;
};

const filterMap = {
  Today: 'today',
  Yesterday: 'yesterday',
  'Last 7 Days': 'last_7_days',
  'This Month': 'this_month',
};

const SummaryCards = ({ dateRange, refreshKey, customStartDate, customEndDate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchKpi = async () => {

      setLoading(true);
      setError(null);
      try {
        // Helper to format date as YYYY-MM-DD in local timezone
        const formatLocalDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        let res;
        if (dateRange === 'Custom Date' && customStartDate && customEndDate) {
          // Use custom date range
          const params = {
            start_date: formatLocalDate(customStartDate),
            end_date: formatLocalDate(customEndDate),
          };
          res = await dashboardAPI.getKpiSummary(null, params);
        } else {
          // Use predefined filter
          const filter = filterMap[dateRange] || undefined;
          res = await dashboardAPI.getKpiSummary(filter);
        }
        setData(res?.data || res || {});
      } catch (err) {
        console.error('Error fetching KPI summary:', err);
        setError('Could not load KPIs');
      } finally {
        setLoading(false);
      }
    };
    fetchKpi();
  }, [dateRange, refreshKey, customStartDate, customEndDate]);

  const totalSales = data?.total_sales || {};
  const netProfit = data?.net_profit || {};
  const creditCustomers = data?.credit_customers || {};
  const totalOrders = data?.total_orders || {};

  // Helper to determine change type based on trend or change_percent
  const getChangeType = (item) => {
    if (item.trend === 'no_change') return 'neutral';
    if (item.trend === 'up') return 'positive';
    if (item.trend === 'down') return 'negative';
    // Fallback to change_percent if trend is not available
    const changePercent = item.change_percent;
    if (changePercent === undefined || changePercent === null || changePercent === 0) return 'neutral';
    return changePercent > 0 ? 'positive' : 'negative';
  };

  // Helper to get detail text based on actual data
  const getSalesDetail = () => {
    if (totalSales.orders !== undefined && totalSales.orders !== null) {
      return totalSales.orders > 0 ? `Based on ${totalSales.orders} orders this period` : 'No orders this period';
    }
    return null;
  };

  const getOrdersDetail = () => {
    const count = totalOrders.count ?? totalOrders.value;
    if (count !== undefined && count !== null) {
      return count > 0 ? `Orders placed: ${count}` : 'No orders placed';
    }
    return null;
  };

  const cards = [
    {
      title: 'Total Sales',
      value: formatValue(totalSales.value, '€'),
      change: formatChange(totalSales.change_percent),
      changeType: getChangeType(totalSales),
      period: 'From last month',
      detail: getSalesDetail(),
      icon: TotalSalesImg,
      color: '#10B981',
    },
    {
      title: 'Net Profit',
      value: formatValue(netProfit.value, '€'),
      change: formatChange(netProfit.change_percent),
      changeType: getChangeType(netProfit),
      period: 'From last month',
      detail: netProfit.value > 0 ? 'With pending credit' : null,
      icon: NetProfitImg,
      color: '#F59E0B',
    },
    {
      title: 'Monthly Credit',
      value: formatValue(creditCustomers.count ?? creditCustomers.value),
      change: formatChange(creditCustomers.change_percent),
      changeType: getChangeType(creditCustomers),
      period: 'From last month',
      detail: (creditCustomers.count ?? creditCustomers.value) > 0 ? 'With pending credit' : null,
      icon: CreditCustomersImg,
      color: '#8B5CF6',
    },
    {
      title: 'Total Orders',
      value: formatValue(totalOrders.count ?? totalOrders.value),
      change: formatChange(totalOrders.change_percent),
      changeType: getChangeType(totalOrders),
      period: totalOrders.period || 'From last month',
      detail: getOrdersDetail(),
      icon: TotalOrdersImg,
      color: '#3B82F6',
    },
  ];

  return (
    <div className="dashboard-summary-grid">
      {cards.map((card, index) => (
        <div key={index} className="dashboard-summary-card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '12px',
            }}
          >
            <div>
              <p style={{ fontSize: '14px', color: '#6E7781', marginBottom: '8px', fontWeight: '400' }}>
                {card.title}
              </p>
              <h3 style={{ fontSize: '22px', fontWeight: '600', color: '#424A53', margin: 0 }}>
                {loading ? (
                  <div style={{ display: 'inline-block', width: '20px', height: '20px', border: '2px solid #E5E7EB', borderTop: '2px solid #3B82F6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                ) : card.value}
              </h3>
            </div>
           {/* <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '4px',
                backgroundColor: `${card.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: card.color,
              }}
            > */}
              <img src={card.icon} alt={card.title} />
            {/* </div> */}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '500',
                color: card.changeType === 'positive' ? '#0B7C5B' : card.changeType === 'negative' ? '#EF3352' : '#6B7280',
                backgroundColor:
                  card.changeType === 'positive'
                    ? 'rgba(11, 124, 91, 0.1)'
                    : card.changeType === 'negative'
                    ? 'rgba(239, 51, 82, 0.08)'
                    : 'rgba(107, 114, 128, 0.1)',
                padding: '2px 8px',
                borderRadius: '100px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {card.changeType === 'positive' && <TrendingUp size={12} />}
              {card.changeType === 'negative' && <TrendingDown size={12} />}
              {card.change}
            </span>
            <span
              style={{
                fontSize: '12px',
                color: card.changeType === 'positive' ? '#0B7C5B' : card.changeType === 'negative' ? '#EF3352' : '#6B7280',
                fontWeight: '400',
              }}
            >
              {card.period}
            </span>
          </div>
          {card.detail && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#9CA3AF' }}>{card.detail}</div>
          )}
          {error && index === 0 && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#EF4444' }}>{error}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SummaryCards;
